import { useEffect, useMemo, useRef, useState, useDeferredValue } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { stockQueueQuery, refreshEcoBadges } from "@/lib/ecoQueries";
import { formatCZK, formatCZKCompact, plur } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StatCard, useIsMobile } from "@/components/shared/PagePrimitives";
import { FilterBar } from "@/components/shared/FilterBar";
import { Package, Wallet, Clock, PackageCheck } from "lucide-react";
import EconomicsDocuments from "@/pages/EconomicsDocuments";
import { toast } from "sonner";
import { ask } from "@/lib/confirm";

// Naskladnění (229, varianta A): KPI · Čeká / Naskladněno · tabulka s Vrátit a Naskladnit.
// Cílový sklad odvozuje server ze zakázky dokladu (bez zakázky centrální sklad).
// Klik na položku nebo číslo dokladu otevře doklad na místě.
const EMPTY = { docs: [], total: 0, count: 0 };
const EMPTY_REC = { rows: [], total: 0, count: 0 };
const RECEIVE_ROLES = ["OWNER", "ASSISTANT", "WAREHOUSE"];      // = WAREHOUSE_OPS_ROLES na serveru
const UNSTOCK_ROLES = ["OWNER", "MANAGER", "ASSISTANT", "ACCOUNTANT"];
const receivedQuery = (year) => ({ queryKey: ["eco-stock-received", year], queryFn: () => api.get(`/documents/stock-received?year=${year}`).then((r) => r.data), retry: false });
const shortDate = (iso) => {
  if (!iso) return "—";
  const [, m, d] = iso.slice(0, 10).split("-");
  return `${Number(d)}. ${Number(m)}.`;
};
const qtyTxt = (q, unit) => `${String(Math.round((q || 0) * 1000) / 1000).replace(".", ",")} ${unit || ""}`.trim();
const money = (v) => (v == null ? "—" : Math.abs(v) >= 1e6 ? formatCZKCompact(v) : formatCZK(v));
const ze = (n) => ([2, 3, 4, 7].includes(n) ? "ze" : "z");
const GRID = "grid-cols-[84px_minmax(0,2fr)_minmax(0,1.4fr)_90px_110px_220px]";

function NoChip({ no, onClick }) {
  return no ? (
    <button type="button" onClick={onClick} title="Otevřít doklad"
      className="inline-flex h-7 items-center rounded-lg bg-secondary px-2 text-[12.5px] font-bold tabular-nums text-foreground/75 whitespace-nowrap hover:text-brand">FP {no}</button>
  ) : <span className="text-muted-foreground text-sm">—</span>;
}

export default function EconomicsStock() {
  const qc = useQueryClient();
  const mobile = useIsMobile();
  const { user } = useAuth();
  const canReceive = RECEIVE_ROLES.includes(user?.role);
  const canUnstock = UNSTOCK_ROLES.includes(user?.role);
  const year = new Date().getFullYear();
  // 220: stejný react-query klíč jako číslovka v obalu Ekonomiky — jeden požadavek.
  const q = useQuery(stockQueueQuery).data || EMPTY;
  const rec = useQuery(receivedQuery(year)).data || EMPTY_REC;
  const [tab, setTab] = useState("wait");
  const [search, setSearch] = useState("");
  const dq = useDeferredValue(search.trim().toLowerCase());
  const [busy, setBusy] = useState(null);           // line_id / "all" — běžící akce
  const openDocRef = useRef(null);
  useEffect(() => {
    const onSaved = () => { refreshEcoBadges(qc); qc.invalidateQueries({ queryKey: ["eco-stock-received"] }); };
    window.addEventListener("pp-doc-saved", onSaved);
    return () => window.removeEventListener("pp-doc-saved", onSaved);
  }, [qc]);
  const changed = () => window.dispatchEvent(new Event("pp-doc-saved"));   // fronta, číslovka i Naskladněno jednou

  const waiting = useMemo(() => (q.docs || []).flatMap((d) => (d.lines || []).map((li) => ({
    ...li, doc_id: d.doc_id, no: d.no, supplier: d.supplier, number: d.number, date: d.date }))), [q]);
  const docCount = new Set(waiting.map((x) => x.doc_id)).size;
  const oldest = waiting.reduce((o, x) => (!o || (x.date || "9") < (o.date || "9") ? x : o), null);

  const match = (x) => !dq || [x.name, x.supplier, x.number, x.no ? `fp ${x.no}` : "", x.no ? `fp${x.no}` : ""].join(" ").toLowerCase().includes(dq.replace(/^#/, ""));
  const rows = (tab === "wait" ? waiting : rec.rows).filter(match);

  const receive = async (x) => {
    setBusy(x.line_id);
    try {
      const { data } = await api.post("/warehouses/receive", { document_id: x.doc_id, line_id: x.line_id });
      toast.success(`Naskladněno${data.warehouse_name ? ` do skladu ${data.warehouse_name}` : ""}.`);
      changed();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const receiveAll = async () => {
    const ids = [...new Set(waiting.map((x) => x.doc_id))];
    if (!await ask(`Naskladnit ${plur(waiting.length, "položku", "položky", "položek")} ${ze(ids.length)} ${plur(ids.length, "dokladu", "dokladů", "dokladů")}? Každá jde do skladu podle zakázky dokladu.`)) return;
    setBusy("all");
    let ok = 0;
    try {
      for (const id of ids) { const { data } = await api.post("/warehouses/receive", { document_id: id }); ok += data.received || 0; }
      toast.success(`Naskladněno ${plur(ok, "položka", "položky", "položek")}.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); changed(); }
  };
  const unstock = async (x) => {
    setBusy(x.line_id);
    try {
      await api.patch(`/documents/${x.doc_id}/lines/${x.line_id}/stock`, { stock_status: "direct" });
      toast.success("Vráceno na přímou spotřebu.");
      changed();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const open = (x) => openDocRef.current?.("faktura", x.doc_id);

  const actions = (x) => tab === "wait" ? (
    <span className="flex justify-end gap-2">
      {canUnstock && <Button variant="outline" disabled={!!busy} onClick={() => unstock(x)} data-testid={`unstock-${x.line_id}`}
        className="h-9 rounded-full font-semibold px-3 max-md:flex-1">Vrátit</Button>}
      {canReceive && <Button disabled={!!busy} onClick={() => receive(x)} data-testid={`receive-${x.line_id}`}
        className="h-9 rounded-full font-bold px-3 max-md:flex-1">Naskladnit</Button>}
    </span>
  ) : (
    <span className="text-[13px] text-muted-foreground md:text-right block truncate">{mobile ? `naskladněno ${shortDate(x.date)}${x.warehouse_name ? ` · ${x.warehouse_name}` : ""}` : x.warehouse_name || "—"}</span>
  );
  const sub = (x) => (tab === "wait" ? (x.unit_price != null ? `${formatCZK(x.unit_price)} / ${x.unit || "ks"}` : x.sku || "") : `naskladněno ${shortDate(x.date)}`);

  return (
    <div className="space-y-5" data-testid="stock-page">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <StatCard icon={Package} label="Čeká na naskladnění" value={plur(q.count, "položka", "položky", "položek")}
          valueClass={q.count ? "text-amber-600 dark:text-amber-400" : ""} testid="stock-queue-count"
          sub={docCount ? `${ze(docCount)} ${plur(docCount, "dokladu", "dokladů", "dokladů")}` : "fronta je prázdná"} />
        <StatCard icon={Wallet} label="Hodnota" value={money(q.total)} title={q.total != null ? formatCZK(q.total) : undefined} sub="bez DPH" testid="stock-queue-total" />
        <StatCard icon={Clock} label="Nejstarší" value={oldest ? shortDate(oldest.date) : "—"} testid="stock-oldest"
          sub={oldest ? [oldest.no ? `FP ${oldest.no}` : null, (oldest.supplier || "").split(/[ ,]/)[0]].filter(Boolean).join(" · ") : ""} />
        <StatCard icon={PackageCheck} label="Naskladněno letos" value={money(rec.total)} title={rec.total != null ? formatCZK(rec.total) : undefined}
          sub={plur(rec.count, "položka", "položky", "položek")} testid="stock-received-total" />
      </div>

      <FilterBar testid="stock-filterbar" mobileIcons
        segments={[{ tabs: [
          { value: "wait", label: "Čeká", count: q.count, testid: "stock-tab-wait" },
          { value: "done", label: "Naskladněno", testid: "stock-tab-done" },
        ], value: tab, onChange: setTab, testid: "stock-tabs" }]}
        search={search} onSearch={setSearch} searchPlaceholder="Položka, dodavatel, FP 147…"
        trailing={!mobile && tab === "wait" && canReceive && waiting.length > 0 && (
          <Button disabled={!!busy} onClick={receiveAll} data-testid="stock-receive-all" className="h-11 rounded-full font-bold px-5">Naskladnit vše</Button>
        )} />

      {mobile && tab === "wait" && canReceive && waiting.length > 0 && (
        <Button disabled={!!busy} onClick={receiveAll} data-testid="stock-receive-all" className="w-full h-11 rounded-full font-bold">Naskladnit vše</Button>
      )}
      <div className="bg-card border border-border rounded-[20px] overflow-hidden" data-testid="stock-queue">
        {!mobile && (
          <div className={`grid ${GRID} gap-3 px-5 py-3.5 border-b border-border text-[12px] font-bold uppercase tracking-[0.05em] text-muted-foreground`}>
            <span>Doklad</span><span>Položka</span><span>Dodavatel</span><span className="text-right">Množství</span><span className="text-right">Částka</span>
            <span className="text-right">{tab === "wait" ? "" : "Sklad"}</span>
          </div>
        )}
        {rows.length === 0 && (
          <p className="px-5 py-6 text-muted-foreground" data-testid="stock-empty">
            {dq ? "Nic neodpovídá hledání." : tab === "wait" ? "Fronta je prázdná — všechno je naskladněné." : `Letos zatím nic naskladněno z dokladů.`}
          </p>
        )}
        {rows.map((x) => mobile ? (
          <div key={x.line_id || x.movement_id} data-testid={`stock-item-${x.line_id || x.movement_id}`} className="px-4 py-3.5 border-b border-border last:border-b-0">
            <button type="button" onClick={() => open(x)} className="w-full text-left grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 items-baseline">
              <span className="font-semibold truncate">{x.name}</span>
              <span className="font-heading font-extrabold tabular-nums whitespace-nowrap">{x.line_total != null || x.total != null ? formatCZK(x.line_total ?? x.total) : ""}</span>
              <span className="text-[13px] text-muted-foreground truncate">{[x.no ? `FP ${x.no}` : null, x.supplier].filter(Boolean).join(" · ")}</span>
              <span className="text-[13px] text-muted-foreground tabular-nums whitespace-nowrap">{qtyTxt(x.qty, x.unit)}</span>
            </button>
            <div className={tab === "wait" ? "mt-2.5" : "mt-1"}>{actions(x)}</div>
          </div>
        ) : (
          <div key={x.line_id || x.movement_id} data-testid={`stock-item-${x.line_id || x.movement_id}`}
            className={`grid ${GRID} gap-3 px-5 py-3 border-b border-border last:border-b-0 items-center hover:bg-muted/40 transition-colors`}>
            <span><NoChip no={x.no} onClick={() => open(x)} /></span>
            <button type="button" onClick={() => open(x)} className="min-w-0 text-left">
              <span className="block font-semibold truncate">{x.name}</span>
              {sub(x) && <span className="block text-[13px] text-muted-foreground truncate">{sub(x)}</span>}
            </button>
            <span className="truncate" title={x.supplier}>{x.supplier || "—"}</span>
            <span className="text-right tabular-nums whitespace-nowrap">{qtyTxt(x.qty, x.unit)}</span>
            <span className="text-right font-heading font-extrabold tabular-nums whitespace-nowrap">{x.line_total != null || x.total != null ? formatCZK(x.line_total ?? x.total) : "—"}</span>
            {actions(x)}
          </div>
        ))}
      </div>

      {/* Detail dokladu na místě (229) */}
      <EconomicsDocuments dialogsOnly openRef={openDocRef} />
    </div>
  );
}
